import { SettingsSection, StackWithDividers } from '@components'

import { Divider, Flex, notify, Spacing, useModal } from '@billy/design-system'
import { ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useDisconnectPayNow } from '@features/paynow/hooks/useDisconnectPayNow'
import { usePayNowOAuthRedirect } from '@features/paynow/hooks/usePayNowOAuthRedirect'
import { usePayNowStatus } from '@features/paynow/hooks/usePayNowStatus'
import { useTrackCardPayment } from '@features/paynow/hooks/useTrackCardPayment'

import { ModalId } from '../../../../../../enums/modalId'
import { NotificationKeys } from '../../../../../../enums/notificationKeys'
import { ActivateOnlinePaymentSection } from './elements/ActivateOnlinePaymentSection'
import { DisconnectStripeAccountSection } from './elements/DisconnectStripeAccountSection'
import { DisconnectStripeAccountModal } from './elements/DisconnectStripeAccountSection/elements/DisconnectStripeAccountModal'
import { EnableCardPayment } from './elements/EnableCardPayment'
import { ManageStripeAccountSection } from './elements/ManageStripeAccountSection'
import { StripeConnectionStatus } from './elements/StripeConnectionStatus'

export const CardPaymentsSettings = (): ReactElement => {
  const { t } = useTranslation()
  const { close: closeDisconnectModal } = useModal(ModalId.DisconnectStripeAccountModal)
  const { isActive, isConnected } = usePayNowStatus()
  const { canRedirect, redirectToOAuth } = usePayNowOAuthRedirect()
  const { trackStripeAccountDisconnected } = useTrackCardPayment()

  const handleDisconnectSuccess = useCallback(() => {
    // The hook owns the async lifecycle and cache invalidation; the connected/disconnected branch is
    // driven by the invalidated PayNow status, so success side-effects are just: close the modal
    // (its section unmounts once the status flips) and notify.
    closeDisconnectModal()
    trackStripeAccountDisconnected()
    notify({
      id: NotificationKeys.StripeAccountDisconnected,
      message: t('settings.organization.card_payments.configuration.disconnect.success_toast'),
      variant: 'success',
    })
  }, [closeDisconnectModal, t, trackStripeAccountDisconnected])

  const { disconnect, isProcessing } = useDisconnectPayNow({ onSuccess: handleDisconnectSuccess })

  return (
    <>
      <SettingsSection>
        <SettingsSection.Content
          sideActions={<StripeConnectionStatus isConnected={isConnected} />}
          title={t('settings.organization.card_payments.configuration.title')}
        >
          <Flex flexDirection="column" gap={Spacing.S16}>
            <StackWithDividers divider={<Divider />}>
              <EnableCardPayment isActive={isActive} isConnected={isConnected} />
              <ManageStripeAccountSection isConnected={isConnected} />
              {isConnected ? (
                <DisconnectStripeAccountSection />
              ) : (
                <ActivateOnlinePaymentSection disabled={!canRedirect} onReconnect={redirectToOAuth} />
              )}
            </StackWithDividers>
          </Flex>
        </SettingsSection.Content>
      </SettingsSection>

      <DisconnectStripeAccountModal
        id={ModalId.DisconnectStripeAccountModal}
        isProcessing={isProcessing}
        onConfirm={disconnect}
      />
    </>
  )
}
